import type { ApiErrorBody, ApiErrorCode } from '@teaplus/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface Options {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  keepalive?: boolean;
}

export const API_EVENTS = {
  unauthenticated: 'teaplus:unauthenticated',
  profileRequired: 'teaplus:profile-required',
  updateRequired: 'teaplus:update-required',
  maintenance: 'teaplus:maintenance',
  restricted: 'teaplus:restricted',
} as const;

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? 'POST' : 'GET');
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      signal: opts.signal,
      keepalive: opts.keepalive,
      headers: {
        accept: 'application/json',
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' ? { 'x-teaplus-request': '1' } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(
      0,
      'UPSTREAM_UNAVAILABLE',
      "Can't reach Tea+. Check your connection and try again.",
    );
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {}
  const code = body?.error.code ?? (res.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL');
  const err = new ApiError(
    res.status,
    code,
    body?.error.message ?? 'Something went wrong. Please try again.',
    body?.error.fields,
  );
  if (code === 'UNAUTHENTICATED' && !path.startsWith('/api/auth/'))
    window.dispatchEvent(new Event(API_EVENTS.unauthenticated));
  if (code === 'PROFILE_REQUIRED') window.dispatchEvent(new Event(API_EVENTS.profileRequired));
  if (code === 'UPDATE_REQUIRED') window.dispatchEvent(new Event(API_EVENTS.updateRequired));
  if (code === 'MAINTENANCE') window.dispatchEvent(new Event(API_EVENTS.maintenance));
  if (code === 'ACCOUNT_RESTRICTED' || code === 'PROFILE_RESTRICTED')
    window.dispatchEvent(new Event(API_EVENTS.restricted));
  throw err;
}

export const isApiError = (e: unknown, code?: ApiErrorCode): e is ApiError =>
  e instanceof ApiError && (!code || e.code === code);

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong. Please try again.';
}
